import {
  Circle,
  ColorMatrixFilter,
  Container,
  Graphics,
  Rectangle,
  Sprite,
  Text,
  type Texture,
} from 'pixi.js';
import type { RealmPlace } from '@goodtime/shared';
import { COLOURS, DISPLAY_FONT, displayStyle, uiStyle, woodGradient } from '../board';
import { paintedPlate } from '../board/BrassButton';
import { pinStatus, spotState, type RealmSpot, type SpotState } from '../../logic/realm/spots';
import { floorPlate } from './RealmPieces';

const BRASS = 0xd9a441;

function icon(texture: Texture | null, size: number): Sprite | null {
  if (!texture) return null;
  const s = new Sprite(texture);
  s.anchor.set(0.5);
  s.scale.set(size / Math.max(texture.width, texture.height));
  return s;
}

/** A painted picture cut to a circle, `radius` across, centred on its
 *  origin. Greyed while its place is locked (`soft`: only dimmed, for a
 *  small picture, where grey would read as noise). */
export class RoundPicture extends Container {
  private sprite: Sprite | null = null;
  private readonly mask0: Graphics;
  private readonly grey = new ColorMatrixFilter();

  constructor(
    private readonly radius: number,
    soft = false,
  ) {
    super();
    this.addChild(new Graphics().circle(0, 0, radius).fill(0x2a1605));
    this.mask0 = new Graphics().circle(0, 0, radius).fill(0xffffff);
    this.addChild(this.mask0);
    if (soft) {
      this.grey.brightness(0.72, false);
    } else {
      this.grey.desaturate();
      this.grey.brightness(0.9, true);
    }
  }

  setTexture(texture: Texture | null): void {
    if (!texture || this.destroyed) return;
    if (!this.sprite) {
      this.sprite = new Sprite(texture);
      this.sprite.anchor.set(0.5);
      this.sprite.mask = this.mask0;
      this.addChildAt(this.sprite, 1);
    } else {
      this.sprite.texture = texture;
    }
    this.sprite.scale.set((this.radius * 2) / Math.min(texture.width, texture.height));
  }

  setGreyed(greyed: boolean): void {
    this.filters = greyed ? [this.grey] : [];
  }
}

export interface PinArt {
  bezel: Texture | null;
  lock: Texture | null;
  coin: Texture | null;
  clock: Texture | null;
  /** The floor of the place's world, under its name (it can come later:
   *  {@link RealmPin.setFloor}). */
  floor?: Texture | null;
}

/**
 * A side place's pin: its painted picture in a brass ring floating over its
 * spot, with its name on a small plate of its world's floor under the
 * ring (as the slots' plates). A locked place
 * is dimmed with a lock (its card gives the price), the warm pool's plate
 * says the wait, and a ready place glows. Kept small, as the map is busy.
 * Drawn at screen size (the map sets its scale to 1 / zoom). Its origin is
 * the spot it points at, at the bottom of the plate.
 */
export class RealmPin extends Container {
  static readonly RING = 20;
  private readonly glow = new Graphics();
  private readonly picture = new RoundPicture(RealmPin.RING - 3, true);
  private readonly ring: Container;
  private readonly plate = new Container();
  private readonly badge = new Container();
  /** The ring and what goes with it (its shadow, glow, picture and badge): slid off the spot only where no stop shows it otherwise. */
  private readonly head = new Container();
  /** From the spot to a slid ring's plate. */
  private readonly stem = new Graphics();
  private ringOff = { x: 0, y: 0 };
  private glowPhase = Math.random() * Math.PI * 2;
  private status = '';
  /** The plate's width (its words, and the timer), and how far it has been moved from under the ring (screen pixels: the strip moves it
   *  to stay clear of the controls; the ring stays on the spot). */
  private plateW = 0;
  private plateOff = { x: 0, y: 0 };
  state: SpotState = 'locked';

  constructor(
    readonly spot: RealmSpot,
    private readonly art: PinArt,
  ) {
    super();
    const cy = -RealmPin.RING - 24;
    this.glow.position.set(0, cy);
    this.picture.position.set(0, cy);
    this.ring = new Container();
    this.ring.position.set(0, cy);
    const bezel = icon(art.bezel, RealmPin.RING * 2 + 6);
    if (bezel) this.ring.addChild(bezel);
    else this.ring.addChild(new Graphics().circle(0, 0, RealmPin.RING).stroke({ color: BRASS, width: 4 }));
    this.badge.position.set(RealmPin.RING * 0.7, cy + RealmPin.RING * 0.7);
    this.head.addChild(
      new Graphics().ellipse(0, cy + RealmPin.RING + 3, RealmPin.RING * 0.8, 5).fill({ color: 0x000000, alpha: 0.3 }),
      this.glow,
      this.picture,
      this.ring,
      this.badge,
    );
    this.addChild(this.stem, this.head, this.plate);
    this.label = `spot:${spot.id}`;
    this.eventMode = 'static';
    this.cursor = 'pointer';
    this.set(null, Date.now());
  }

  setPicture(texture: Texture | null): void {
    this.picture.setTexture(texture);
  }

  /** The floor of the place's world has arrived. */
  setFloor(texture: Texture | null): void {
    if (!texture || texture === this.art.floor || this.destroyed) return;
    this.art.floor = texture;
    this.drawPlate(this.status);
  }

  /** The plate's coin and clock, once loaded (drawn next time it's set). */
  setIcons(coin: Texture | null, clock: Texture | null): void {
    this.art.coin = coin;
    this.art.clock = clock;
  }

  /** Show the place as it stands now (null: not loaded yet, so locked
   *  with no price). */
  set(place: RealmPlace | null, now: number): void {
    this.state = place ? spotState(place, now) : 'locked';
    const status = place ? pinStatus(place, now) : '';
    this.picture.setGreyed(this.state === 'locked');
    this.picture.alpha = this.state === 'done' ? 0.7 : 1;
    this.ring.alpha = this.state === 'done' ? 0.8 : 1;
    for (const c of this.badge.removeChildren()) c.destroy();
    if (this.state === 'locked') {
      const lock = icon(this.art.lock, 16);
      if (lock) this.badge.addChild(lock);
    }
    this.drawPlate(this.state === 'waiting' ? status : '');
    this.glow.visible = this.state === 'ready';
    if (this.glow.visible) this.drawGlow(1);
    this.updateHit();
  }

  /** The tap area: the ring and the plate (where the plate has been moved to, both). A moved plate's area is the two apart, not one box
   *  round both: that box would reach over the gap onto a neighbour's ring. */
  private updateHit(): void {
    const top = -RealmPin.RING * 2 - 28;
    const { x, y } = this.plateOff;
    const r = this.ringOff;
    if (x === 0 && y === 0 && r.x === 0 && r.y === 0) {
      this.hitArea = new Rectangle(-Math.max(RealmPin.RING + 6, this.plate.width / 2), top, Math.max(RealmPin.RING * 2 + 12, this.plate.width), -top);
      return;
    }
    const ring = new Rectangle(-(RealmPin.RING + 6) + r.x, top + r.y, RealmPin.RING * 2 + 12, -top - 18);
    const plate = new Rectangle(-this.plateW / 2 + x, -22 + y, this.plateW, 22);
    this.hitArea = { contains: (px: number, py: number) => ring.contains(px, py) || plate.contains(px, py) };
  }

  /** Slide the ring off its spot, screen pixels, with a stem from the spot to its plate: only for a place no stop shows otherwise
   *  (overviewPlaces `RING_SLIDE`). The plate's offset is given apart, from the spot. */
  setRingOffset(x: number, y: number): void {
    if (x === this.ringOff.x && y === this.ringOff.y) return;
    this.ringOff = { x, y };
    this.head.position.set(x, y);
    this.stem.clear();
    if (x !== 0 || y !== 0) {
      this.stem
        .moveTo(0, 0)
        .lineTo(x, y - 2)
        .stroke({ color: BRASS, width: 2, alpha: 0.9 })
        .circle(0, 0, 3)
        .fill({ color: BRASS });
    }
    this.updateHit();
  }

  /** How far the ring has slid from its spot (tests). */
  get ringOffsetNow(): { x: number; y: number } {
    return this.ringOff;
  }

  /** Move the name plate from where the spot puts it, screen pixels (the ring stays on the spot, unless it slid). */
  setPlateOffset(x: number, y: number): void {
    if (x === this.plateOff.x && y === this.plateOff.y) return;
    this.plateOff = { x, y };
    this.plate.position.set(x, -20 - 2 + y);
    this.updateHit();
  }

  /** How far the plate has been moved from under the ring (tests). */
  get plateOffsetNow(): { x: number; y: number } {
    return this.plateOff;
  }

  /** The plate's width now (its words and timer), for the strip's placing. */
  get plateWidth(): number {
    return this.plateW;
  }

  /** The ring's middle, in the pin's own pixels (the spot is the origin). */
  ringCentre(): { x: number; y: number } {
    return { x: this.ringOff.x, y: -RealmPin.RING - 24 + this.ringOff.y };
  }

  /** The glow's breathing, `dt` ms on. */
  tick(dt: number): void {
    if (!this.glow.visible) return;
    this.glowPhase += dt / 520;
    this.drawGlow(0.75 + 0.25 * Math.sin(this.glowPhase));
  }

  private drawGlow(k: number): void {
    const r = RealmPin.RING;
    this.glow.clear();
    for (const [grow, alpha] of [
      [12, 0.16],
      [8, 0.26],
      [4, 0.42],
    ] as const) {
      this.glow.circle(0, 0, r + grow * k).fill({ color: 0xffd25a, alpha });
    }
  }

  private drawPlate(status: string): void {
    this.status = status;
    for (const c of this.plate.removeChildren()) c.destroy({ children: true });
    const locked = this.state === 'locked';
    const parts: Container[] = [];
    const name = new Text({ text: this.spot.name, style: displayStyle(12, locked ? 0xe8dcc4 : 0xffe48a) });
    name.anchor.set(0.5);
    parts.push(name);
    if (status) {
      const pic = icon(locked ? this.art.coin : this.art.clock, 13);
      if (pic) parts.push(pic);
      const t = new Text({
        text: status,
        style: { fontFamily: DISPLAY_FONT, fontSize: 12, fill: locked ? 0xffe48a : 0xbfe8ff, stroke: { color: 0x1a2a3a, width: 2 } },
      });
      t.anchor.set(0.5);
      parts.push(t);
    }
    const gap = 4;
    const inner = parts.reduce((a, c) => a + c.width, 0) + gap * (parts.length - 1);
    const w = inner + 14;
    const h = 20;
    this.plateW = w;
    const bg = floorPlate(new Graphics(), { x: -w / 2, y: 0, w, h }, this.art.floor ?? null, {
      dim: locked,
      edge: locked ? 0x8a7458 : BRASS,
    });
    this.plate.addChild(bg);
    let x = -inner / 2;
    for (const c of parts) {
      c.position.set(x + c.width / 2, h / 2);
      this.plate.addChild(c);
      x += c.width + gap;
    }
    this.plate.position.set(this.plateOff.x, -h - 2 + this.plateOff.y);
  }
}

/**
 * A secret on the painting: a small gold sparkle that twinkles now and then.
 * Drawn at screen size (scale 1 / zoom); tap it to find the secret.
 */
export class RealmGlint extends Container {
  private star: Container;
  private readonly halo = new Graphics();
  private phase = Math.random() * 10;
  private base = 1;
  private blocked = false;
  private spent = false;
  /** How shown it is, 0 to 1 (it fades in and out over {@link RealmGlint.FADE_MS}). */
  private level = 1;

  constructor(readonly secretId: string) {
    super();
    this.halo.circle(0, 0, 15).fill({ color: 0xfff1c0, alpha: 0.4 }).circle(0, 0, 9).fill({ color: 0xffffff, alpha: 0.35 });
    this.star = new Graphics().star(0, 0, 4, 11, 3).fill(0xffe48a);
    this.addChild(this.halo, this.star);
    this.label = `glint:${secretId}`;
    this.eventMode = 'static';
    this.cursor = 'pointer';
    this.hitArea = new Circle(0, 0, 24);
  }

  static readonly FADE_MS = 150;

  /** The map has something over this glint now (a control, a name, a pin) or no longer: while it does, it takes no tap and fades out
   *  (at once when `immediate`, for reduced motion and the first placing); a glint already found is not touched. */
  setBlocked(blocked: boolean, immediate = false): void {
    if (blocked === this.blocked && !immediate) return;
    this.blocked = blocked;
    this.eventMode = blocked || this.spent ? 'none' : 'static';
    if (immediate) this.level = blocked ? 0 : 1;
    this.drawLevel();
  }

  /** Fade towards shown or hidden, `ms` on. */
  fade(ms: number): void {
    if (this.spent) return;
    const target = this.blocked ? 0 : 1;
    if (this.level === target) return;
    const step = ms / RealmGlint.FADE_MS;
    this.level = this.level < target ? Math.min(target, this.level + step) : Math.max(target, this.level - step);
    this.drawLevel();
  }

  private drawLevel(): void {
    if (this.spent) return;
    this.alpha = this.level;
    this.renderable = this.level > 0;
  }

  /** Its level (tests). */
  get shownLevel(): number {
    return this.level;
  }

  /** The secret has been found: the glint takes no more taps (it fades out). */
  spend(): void {
    this.spent = true;
    this.eventMode = 'none';
    this.renderable = true;
  }

  get isBlocked(): boolean {
    return this.blocked;
  }

  /** The painted sparkle, once loaded, in place of the drawn star. */
  setSparkle(texture: Texture | null): void {
    const pic = icon(texture, 30);
    if (!pic) return;
    this.star.destroy();
    this.star = pic;
    this.base = pic.scale.x;
    this.addChild(pic);
  }

  /** Twinkle: mostly a soft shimmer, with a bright flash every few seconds. */
  tick(dt: number): void {
    this.phase += dt / 1000;
    const cycle = this.phase % 3.2;
    const flash = cycle < 0.5 ? Math.sin((cycle / 0.5) * Math.PI) : 0;
    const shimmer = 0.72 + 0.14 * Math.sin(this.phase * 2.3);
    this.star.alpha = Math.min(1, shimmer + flash);
    this.star.scale.set(this.base * (0.85 + 0.35 * flash));
    this.star.rotation = flash * 0.4;
    this.halo.alpha = 0.45 + 0.55 * flash;
    this.halo.scale.set(0.8 + 0.6 * flash);
  }

}

/**
 * The map's title, "The Goodtime Realms", on a painted wood plate, shown on
 * the map's first view only. Its origin is its centre.
 */
export class RealmTitlePlaque extends Container {
  constructor(title: string) {
    super();
    const text = new Text({ text: title, style: { ...displayStyle(30), letterSpacing: 1 } });
    text.anchor.set(0.5);
    const sub = new Text({ text: 'Tap a place to explore', style: uiStyle(13, COLOURS.cream, '600') });
    sub.anchor.set(0.5);
    const w = Math.max(text.width, sub.width) + 64;
    const h = 78;
    const plate = paintedPlate('wood', w, h);
    if (plate) {
      plate.position.set(-plate.width * plate.scale.x / 2, -h / 2);
      this.addChild(plate);
    } else {
      this.addChild(
        new Graphics()
          .roundRect(-w / 2, -h / 2 + 3, w, h, 14)
          .fill({ color: 0x000000, alpha: 0.4 })
          .roundRect(-w / 2, -h / 2, w, h, 14)
          .fill(woodGradient())
          .stroke({ color: BRASS, width: 2 }),
      );
    }
    text.position.set(0, -9);
    sub.position.set(0, 20);
    sub.alpha = 0.9;
    this.addChild(text, sub);
    this.label = 'realm-title';
    // A title, not a dialog: taps go through to the map under it.
    this.eventMode = 'none';
  }
}
